"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { ReactNode, useCallback, useMemo } from "react"

import FacetBar from "@modules/store/components/facet-bar"
import {
  FacetGroup,
  FacetKey,
} from "@modules/store/components/facet-list"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

// Serializable per-product data the client uses to facet/sort/search.
export type StoreFacetItem = {
  id: string
  title: string
  colorTag?: string
  material?: string
  finish?: string
  look?: string
  size?: string
  minPrice: number
  createdAt: number
}

type StoreViewProps = {
  items: StoreFacetItem[]
  previews: Record<string, ReactNode>
  sortBy: SortOptions
  query?: string
}

const FACET_DEFS: { key: FacetKey; label: string; field: keyof StoreFacetItem }[] =
  [
    { key: "color", label: "Color", field: "colorTag" },
    { key: "material", label: "Material", field: "material" },
    { key: "finish", label: "Finish", field: "finish" },
    { key: "look", label: "Look / Shape", field: "look" },
    { key: "size", label: "Size", field: "size" },
  ]

const FACET_KEYS = FACET_DEFS.map((d) => d.key)

// Some entry points (mega-menu, homepage) link to broad color labels like
// "Beige & Cream" or "Black & White" that don't match a single colorTag.
// Match leniently: a selection matches a product if any word overlaps.
const looseColorMatch = (selectedValue: string, colorTag?: string) => {
  if (!colorTag) return false
  const sel = selectedValue.toLowerCase()
  const tag = colorTag.toLowerCase()
  if (sel === tag) return true
  if (sel.includes(tag) || tag.includes(sel)) return true
  const selWords = sel.split(/[^a-z]+/).filter(Boolean)
  return selWords.includes(tag)
}

const matchesFacet = (
  key: FacetKey,
  selectedValues: string[],
  item: StoreFacetItem
) => {
  if (selectedValues.length === 0) return true
  const field = FACET_DEFS.find((d) => d.key === key)!.field
  const itemValue = item[field] as string | undefined
  if (key === "color") {
    return selectedValues.some((v) => looseColorMatch(v, itemValue))
  }
  return !!itemValue && selectedValues.includes(itemValue)
}

const StoreView = ({ items, previews, sortBy, query }: StoreViewProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Read selected facet values from URL (comma-separated per key).
  const selected = useMemo(() => {
    const result = {} as Record<FacetKey, string[]>
    for (const key of FACET_KEYS) {
      const raw = searchParams.get(key)
      result[key] = raw
        ? raw
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : []
    }
    return result
  }, [searchParams])

  const hasActive = FACET_KEYS.some((k) => selected[k].length > 0)

  // Update URL preserving q + sortBy + other facets.
  const pushParams = useCallback(
    (next: Record<FacetKey, string[]>) => {
      const params = new URLSearchParams(searchParams)
      for (const key of FACET_KEYS) {
        if (next[key].length > 0) {
          params.set(key, next[key].join(","))
        } else {
          params.delete(key)
        }
      }
      // Filtering changes the result set — reset pagination.
      params.delete("page")
      const qs = params.toString()
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [pathname, router, searchParams]
  )

  // A color chip is active when any selected color value loosely matches it, so
  // broad/lower-case entry-point values ("beige & cream", "white") light up the
  // canonical chip ("Cream", "White") instead of spawning a phantom one.
  const isOptionActive = useCallback(
    (key: FacetKey, value: string) => {
      if (key === "color") {
        return selected.color.some((v) => looseColorMatch(v, value))
      }
      return selected[key].includes(value)
    },
    [selected]
  )

  const handleToggle = useCallback(
    (key: FacetKey, value: string) => {
      const current = selected[key]
      // For color, toggling a chip that's active via a loose match removes the
      // matching selection(s) rather than appending a redundant exact value.
      if (key === "color") {
        const matching = current.filter((v) => looseColorMatch(v, value))
        const nextForKey = matching.length
          ? current.filter((v) => !matching.includes(v))
          : [...current, value]
        pushParams({ ...selected, color: nextForKey })
        return
      }
      const nextForKey = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      pushParams({ ...selected, [key]: nextForKey })
    },
    [selected, pushParams]
  )

  const handleClearAll = useCallback(() => {
    const cleared = {} as Record<FacetKey, string[]>
    for (const key of FACET_KEYS) cleared[key] = []
    pushParams(cleared)
  }, [pushParams])

  const setSort = useCallback(
    (value: SortOptions) => {
      const params = new URLSearchParams(searchParams)
      params.set("sortBy", value)
      const qs = params.toString()
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [pathname, router, searchParams]
  )

  // Apply search (q) up front — facet counts should reflect the searched set.
  const searchedItems = useMemo(() => {
    if (!query) return items
    const q = query.toLowerCase()
    return items.filter((it) => {
      const haystack = [
        it.title,
        it.colorTag,
        it.material,
        it.finish,
        it.look,
        it.size,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [items, query])

  // Filtered grid: every facet group must match.
  const filtered = useMemo(() => {
    return searchedItems.filter((it) =>
      FACET_KEYS.every((key) => matchesFacet(key, selected[key], it))
    )
  }, [searchedItems, selected])

  // Sort the filtered set client-side.
  const sorted = useMemo(() => {
    const arr = [...filtered]
    if (sortBy === "price_asc") {
      arr.sort((a, b) => a.minPrice - b.minPrice)
    } else if (sortBy === "price_desc") {
      arr.sort((a, b) => b.minPrice - a.minPrice)
    } else {
      arr.sort((a, b) => b.createdAt - a.createdAt)
    }
    return arr
  }, [filtered, sortBy])

  // Build facet groups with counts. A group's option count reflects what would
  // remain if that option were selected, holding the OTHER groups' selections.
  const groups: FacetGroup[] = useMemo(() => {
    return FACET_DEFS.map((def) => {
      const field = def.field
      // Items that satisfy every group EXCEPT this one.
      const baseItems = searchedItems.filter((it) =>
        FACET_KEYS.filter((k) => k !== def.key).every((k) =>
          matchesFacet(k, selected[k], it)
        )
      )
      const counts = new Map<string, number>()
      for (const it of baseItems) {
        const value = it[field] as string | undefined
        if (!value) continue
        counts.set(value, (counts.get(value) ?? 0) + 1)
      }
      // Ensure currently-selected values stay visible even at count 0 — but for
      // color, skip values already covered by a canonical option via loose match
      // (so "beige & cream" doesn't add a phantom chip next to "Cream").
      for (const v of selected[def.key]) {
        if (counts.has(v)) continue
        if (
          def.key === "color" &&
          Array.from(counts.keys()).some((opt) => looseColorMatch(v, opt))
        ) {
          continue
        }
        counts.set(v, 0)
      }
      const options = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value))
      return { key: def.key, label: def.label, options }
    })
  }, [searchedItems, selected])

  return (
    <div className="py-6 content-container" data-testid="category-container">
      <div className="mb-6">
        {query ? (
          <>
            <p className="text-sm text-muted">Results for</p>
            <h1
              className="font-serif text-3xl text-ink"
              data-testid="store-page-title"
            >
              “{query}”
            </h1>
          </>
        ) : (
          <h1
            className="font-serif text-3xl text-ink"
            data-testid="store-page-title"
          >
            All tile
          </h1>
        )}
        <p className="text-sm text-muted mt-1" data-testid="product-count">
          {sorted.length} {sorted.length === 1 ? "tile" : "tiles"}
        </p>
      </div>

      {/* Top filter bar: one checkable dropdown per facet + sort + clear all */}
      <FacetBar
        groups={groups}
        onToggle={handleToggle}
        onClearAll={handleClearAll}
        hasActive={hasActive}
        isOptionActive={isOptionActive}
        selectedCount={(key) => selected[key].length}
        totalSelected={FACET_KEYS.reduce((n, k) => n + selected[k].length, 0)}
        sortBy={sortBy}
        onSort={setSort}
      />

      {sorted.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-ink font-medium">
            {query ? `No tile matched “${query}”.` : "No tile found."}
          </p>
          <p className="text-muted text-sm mt-1">
            Try a different look, color, or material.
          </p>
          {hasActive && (
            <button
              type="button"
              onClick={handleClearAll}
              className="mt-4 inline-flex items-center rounded-full border border-line bg-card px-4 py-2 text-sm font-medium text-ink hover:border-clay hover:text-clay transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <ul
          className="grid grid-cols-2 w-full small:grid-cols-3 medium:grid-cols-4 gap-x-6 gap-y-8"
          data-testid="products-list"
        >
          {sorted.map((it) => (
            <li key={it.id}>{previews[it.id]}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default StoreView
